import React from 'react';
import './payment-receipt.css';
import { VBotLogo } from '../ui/vbot-logo';
import {
  INVOICE_COMPANY,
  buildInvoiceLineItems,
  computeDueDate,
  formatAmount,
  formatShortDate,
  resolveInvoiceNumber,
} from '../../utils/invoiceHelpers';

export {
  INVOICE_COMPANY,
  buildInvoiceLineItems,
  formatAmount,
  formatShortDate,
  resolveInvoiceNumber,
} from '../../utils/invoiceHelpers';

export function PaymentReceiptDocument({
  payment,
  onPrint,
  onDownload,
  onDownloadPdf,
  onShare,
  onBack,
  onSecondary,
  backLabel = 'Done',
  secondaryLabel = 'Subscribe again',
  className = '',
}) {
  if (!payment) return null;

  const isPaid = String(payment.status || '').toLowerCase() === 'completed';
  const invoiceNumber = resolveInvoiceNumber(payment);
  const invoiceDate = payment.created_at ? new Date(payment.created_at) : new Date();
  const dateLabel = formatShortDate(invoiceDate);
  const dueDateLabel = computeDueDate(invoiceDate, isPaid);
  const terms = isPaid ? 'Paid' : 'Net 15';
  const totalLabel = formatAmount(payment.amount);
  const lineItems = buildInvoiceLineItems(payment);
  const customerName = payment.customer_name || 'Customer';
  const customerEmail = payment.customer_email || '';

  return (
    <div className={`vps-receipt-print-root ${className}`}>
      <article className="vps-invoice-page">
        <header className="vps-invoice-header">
          <div className="vps-invoice-company">
            <div className="vps-invoice-brand-row">
              <VBotLogo size="md" className="vps-receipt-brand-logo" />
              <strong>{INVOICE_COMPANY.legalName}</strong>
            </div>
            <span>{INVOICE_COMPANY.addressLine1}</span>
            <span>{INVOICE_COMPANY.addressLine2}</span>
            <a href={`mailto:${INVOICE_COMPANY.email}`}>{INVOICE_COMPANY.email}</a>
          </div>
          <h1 className="vps-invoice-title">INVOICE</h1>
        </header>

        <div className="vps-invoice-meta-grid">
          <div className="vps-invoice-address-block">
            <div className="vps-invoice-address-label">BILL TO</div>
            <div className="vps-invoice-address-body">
              {customerName}
              {customerEmail && (
                <>
                  <br />
                  {customerEmail}
                </>
              )}
            </div>
          </div>
          <div className="vps-invoice-address-block">
            <div className="vps-invoice-address-label">SHIP TO</div>
            <div className="vps-invoice-address-body">
              {customerName}
              {customerEmail && (
                <>
                  <br />
                  {customerEmail}
                </>
              )}
            </div>
          </div>
          <dl className="vps-invoice-details">
            <div className="vps-invoice-detail-row">
              <dt>INVOICE #</dt>
              <dd>{invoiceNumber}</dd>
            </div>
            <div className="vps-invoice-detail-row">
              <dt>DATE</dt>
              <dd>{dateLabel}</dd>
            </div>
            <div className="vps-invoice-detail-row">
              <dt>DUE DATE</dt>
              <dd>{dueDateLabel}</dd>
            </div>
            <div className="vps-invoice-detail-row">
              <dt>TERMS</dt>
              <dd>{terms}</dd>
            </div>
          </dl>
        </div>

        <div className="vps-invoice-table-wrap">
          <table className="vps-invoice-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Activity</th>
                <th scope="col">Description</th>
                <th scope="col" className="num">Qty</th>
                <th scope="col" className="num">Rate</th>
                <th scope="col" className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((row) => (
                <tr key={`${row.date}-${row.description}`}>
                  <td>{row.date}</td>
                  <td>{row.activity}</td>
                  <td>{row.description}</td>
                  <td className="num">{row.qty}</td>
                  <td className="num">{row.rate}</td>
                  <td className="num">{row.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className="vps-invoice-footer">
          <div className="vps-invoice-pay-note">
            <strong>Ways to pay</strong>
            <p>{isPaid ? 'Payment received — thank you.' : 'Pay online using your receipt link.'}</p>
          </div>
          <div className="vps-invoice-balance">
            <div className="vps-invoice-balance-label">BALANCE DUE</div>
            <div className="vps-invoice-balance-value">{isPaid ? '$0.00' : totalLabel}</div>
            {isPaid && (
              <div className="vps-invoice-paid-badge">PAID {totalLabel}</div>
            )}
          </div>
        </footer>

        <div className="vps-invoice-page-no" aria-hidden="true">
          — 1 of 1 —
        </div>

        {(onBack || onSecondary || onPrint || onDownload || onDownloadPdf || onShare) && (
          <div className="vps-receipt-actions vps-receipt-no-print">
            {onBack && (
              <button type="button" onClick={onBack} className="vps-btn-done">
                {backLabel}
              </button>
            )}
            <div className="vps-receipt-actions-secondary">
              {onShare && (
                <button type="button" onClick={onShare} className="vps-btn-link">
                  Share receipt
                </button>
              )}
              {onDownloadPdf && (
                <button type="button" onClick={onDownloadPdf} className="vps-btn-link">
                  Download PDF
                </button>
              )}
              {onDownload && (
                <button type="button" onClick={onDownload} className="vps-btn-link">
                  Download HTML
                </button>
              )}
              {onPrint && (
                <button type="button" onClick={onPrint} className="vps-btn-print-link">
                  Print
                </button>
              )}
              {onSecondary && (
                <button type="button" onClick={onSecondary} className="vps-btn-link">
                  {secondaryLabel}
                </button>
              )}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}

export default PaymentReceiptDocument;
